/**
 * Admin subscription management endpoints.
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.7).
 *
 *   - GET  /admin/subscriptions/stats           — global counters
 *   - GET  /admin/subscriptions                  — search + paginate
 *   - GET  /admin/subscriptions/{id}             — detail
 *   - PUT  /admin/subscriptions/{id}/status      — change status
 *   - PUT  /admin/subscriptions/{id}/skip        — skip next charge
 *   - PUT  /admin/subscriptions/{id}/frequency   — change cadence
 *   - PUT  /admin/subscriptions/{id}/address     — update address
 *   - PUT  /admin/subscriptions/{id}/reschedule  — change next billing date
 *   - PUT  /admin/subscriptions/{id}/discount    — change discount %
 *   - POST /admin/subscriptions/{id}/note        — append internal note
 *   - PUT  /admin/subscriptions/{id}/resolve     — mark as resolved
 *   - PUT  /admin/subscriptions/{id}/products    — replace product list
 *
 * Re-exported from services/api.ts so existing call-sites keep working.
 */

import { apiClient } from '../client';

// ─── Shapes ─────────────────────────────────────────────────────────────────

export interface SubscriptionSearchResult {
  id: string;
  lead_id: string | null;
  platform: string;
  customer_email: string;
  customer_name: string | null;
  phone: string | null;
  dog_name: string;
  status: string | null;
  next_billing_date: string | null;
  // true = nació en la calculadora (funnel); false = creado directo en Shopify;
  // null = no aplica (PrestaShop).
  via_calculator: boolean | null;
  created_at: string | null;
  // Appstle lleva la cuenta de pedidos servidos y el importe del contrato;
  // PrestaShop no, así que en sus filas llegan a null.
  total_orders: number | null;
  amount: number | null;
  currency: string | null;
  lifetime_value: number | null;
  last_order_date: string | null;
  // Frecuencia en días, homogénea entre plataformas (Appstle la da por
  // intervalo + unidad, PrestaShop ya en días).
  frequency_days: number | null;
  origin_order: string | number | null;
  // Estado de cobro: solo PrestaShop lo expone.
  retry_count: number | null;
  last_payment_error: string | null;
}

export interface SubscriptionSearchResponse {
  total: number;
  page: number;
  size: number;
  results: SubscriptionSearchResult[];
  partial?: boolean;
  errors?: string[];
}

export interface SubscriptionProduct {
  name: string;
  variant_id: string;
  quantity: number;
  price: number;
}

export interface SubscriptionDetail {
  id: string;
  platform: string;
  status: string;
  products: SubscriptionProduct[];
  next_billing_date: string | null;
  frequency_days: number | null;
  delivery_address: Record<string, string>;
  customer_email: string;
  customer_name: string;
  order_history?: Array<Record<string, unknown>>;
  created_at: string | null;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

// Reparto de un contador entre plataformas.
export interface SubscriptionStatSplit {
  total: number;
  shopify: number;
  prestashop: number;
}

export interface SubscriptionStats {
  cards: {
    active: SubscriptionStatSplit;
    paused: SubscriptionStatSplit;
    cancelled: SubscriptionStatSplit;
    total: SubscriptionStatSplit;
  };
  // `sin_dato` = no se pudo resolver el origen; no es un "no".
  calculator: { si: number; no: number; sin_dato: number };
  partial?: boolean;
  errors?: string[];
  error?: string;
}

export const adminGetSubscriptionStats = async (params?: {}): Promise<SubscriptionStats> => {
  const response = await apiClient.get<SubscriptionStats>('/admin/subscriptions/stats', { params });
  return response.data;
};

export const adminSearchSubscriptions = async (params: {
  q?: string;
  status?: string;
  platform?: string;
  // true = solo las nacidas en la calculadora; false = solo el resto.
  via_calculator?: boolean;
  catalog_kind?: 'product' | 'uncatalogued';
  page?: number;
  size?: number;
}): Promise<SubscriptionSearchResponse> => {
  const response = await apiClient.get<SubscriptionSearchResponse>('/admin/subscriptions', { params });
  return response.data;
};

export const adminGetSubscriptionDetail = async (subId: string): Promise<SubscriptionDetail> => {
  const response = await apiClient.get<SubscriptionDetail>(`/admin/subscriptions/${subId}`);
  return response.data;
};

export const adminUpdateSubscriptionStatus = async (
  subId: string,
  status: string,
  reason?: string,
): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/status`, {
    status,
    reason,
  });
  return response.data;
};

export const adminSkipSubscription = async (subId: string): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/skip`);
  return response.data;
};

export const adminUpdateSubscriptionFrequency = async (
  subId: string,
  days: number,
): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/frequency`, {
    days,
  });
  return response.data;
};

export const adminUpdateSubscriptionAddress = async (
  subId: string,
  address: Record<string, string>,
): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/address`, {
    address,
  });
  return response.data;
};

export const adminRescheduleSubscription = async (subId: string, newDate: string): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/reschedule`, {
    new_date: newDate,
  });
  return response.data;
};

export const adminUpdateSubscriptionDiscount = async (
  subId: string,
  discount: number,
): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/discount`, {
    discount,
  });
  return response.data;
};

export const adminAddSubscriptionNote = async (subId: string, note: string): Promise<Record<string, unknown>> => {
  const response = await apiClient.post(`/admin/subscriptions/${subId}/note`, {
    note,
  });
  return response.data;
};

export const adminResolveSubscription = async (subId: string, note?: string): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/resolve`, {
    note,
  });
  return response.data;
};

export const adminUpdateSubscriptionProducts = async (
  subId: string,
  products: Array<{ variant_id: string; quantity: number; price: number }>,
): Promise<Record<string, unknown>> => {
  const response = await apiClient.put(`/admin/subscriptions/${subId}/products`, {
    products,
  });
  return response.data;
};
